import React, { useEffect, useState } from "react";
import { Col, Row, Table, Spinner, Card, Badge, Container } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import { FaArrowLeft } from "react-icons/fa";

const POPIView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState({
        podate: '',
        enquiryno: '',
        enquirydate: '',
        pono: '',
        status: '',
        shipto: '',
        paymentterms: '',
        deliveryterm: '',
        supplier: null,
        supplierDetails: {},
        supplierMultipleAddress: null,
        branch: null,
        warehouse: null,
        grandtotalinr: 0,
        grandtotalcurrency: 0,
        rows: []
    });

    const unitOptions = [
        { value: "", label: "Select UOM" },
        { value: "bot", label: "Bot" },
        { value: "ctn", label: "Ctn" },
        { value: "days", label: "Days" },
        { value: "ft", label: "Ft" },
        { value: "hrs", label: "Hrs" },
        { value: "kg", label: "Kg" },
        { value: "ltr", label: "Ltr" },
        { value: "mtr", label: "Mtr" },
        { value: "nos", label: "Nos" },
        { value: "pil", label: "Pil" },
        { value: "pkt", label: "Pkt" },
        { value: "set", label: "Set" },
        { value: "pcs", label: "Pcs" }
    ];

    const currencyOptions = [
        { value: "", label: "Select Currency" },
        { value: "$ (USD)", label: "USD" },
        { value: "€ (EUR)", label: "EUR" },
        { value: "¥ (JPY)", label: "JPY" },
        { value: "£ (GBP)", label: "GBP" },
        { value: "₹ (INR)", label: "INR" },
    ];

    useEffect(() => {
        fetchPOData();
    }, []);

    const fetchPOData = async () => {
        try {
            setIsLoading(true);
            const { data } = await axios.get(`${BASE_URL}purchaseOrder/findById/${id}`, {
                headers: authHeader(),
            });

            setState({
                pono: data.pono || '',
                status: data.status || '',
                podate: data.podate || '',
                shipto: data.shipto || '',
                paymentterms: data.paymentterms || '',
                deliveryterm: data.deliveryterm || '',
                enquiryno: data.enquiryno || '',
                enquirydate: data.enquirydate || '',
                grandtotalinr: data.grandtotalinr || 0,
                grandtotalcurrency: data.grandtotalcurrency || 0,
                supplier: data.supplier ? { value: data.supplier.id, label: data.supplier.companyname } : null,
                supplierDetails: {
                    contactperson: data.contactperson || '',
                    contactno: data.contactno || '',
                    email: data.email || ''
                },
                supplierMultipleAddress: data.supplierMultipleAddress ? {
                    value: data.supplierMultipleAddress.id,
                    label: data.supplierMultipleAddress.supplieraddress
                } : null,
                branch: data.branch ? { value: data.branch.id, label: data.branch.branchname } : null,
                warehouse: data.warehouse ? { value: data.warehouse.id, label: data.warehouse.warehousename } : null,
                rows: data.purchaseOrderItems?.map(item => ({
                    product: { name: item.product.description },
                    quantity: item.quantity,
                    unitrateinr: item.unitrateinr,
                    totalinr: item.totalinr,
                    totalcurrency: item.totalcurrency,
                    uom: item.uom,
                    currency: item.currency,
                    unitratecurrency: item.unitratecurrency,
                    productremarks: item.productremarks,
                    partno: item.partno,
                    hsnsacno: item.hsnsacno,
                    serialnumber: item.serialnumber,
                    modelnumber: item.modelnumber,
                    tagnumber: item.tagnumber,
                    moc: item.moc,
                    itemcode: item.itemcode
                })) || []
            });
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch PO data.' });
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Draft': return 'secondary';
            case 'Submitted': return 'primary';
            case 'Approved': return 'success';
            case 'Rejected': return 'danger';
            default: return 'info';
        }
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(value || 0);

    const formatForeignCurrency = (value, currency) => {
        if (!value) return '-';
        return `${parseFloat(value).toFixed(2)} ${currency || ''}`;
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-primary mb-0">INTERNATIONAL PURCHASE ORDER - VIEW</h5>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                            <FaArrowLeft className="me-1" />
                            Back
                        </button>
                    </div>
                    
                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-primary ps-2">
                                <small className="text-muted">PO No</small>
                                <div className="fw-bold">{state.pono}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">PO Date</small>
                                <div className="fw-bold">{formatDate(state.podate)}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Enquiry No</small>
                                <div className="fw-bold">{state.enquiryno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge 
                                        bg={getStatusBadge(state.status)} 
                                        className="px-2 py-1"
                                    >
                                        {state.status}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Supplier Information */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Supplier Details</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Company Name</small>
                                <div className="fw-bold">{state.supplier?.label || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Contact Person</small>
                                <div className="fw-bold">{state.supplierDetails.contactperson || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Phone</small>
                                <div className="fw-bold">{state.supplierDetails.contactno || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Email</small>
                                <div className="fw-bold">{state.supplierDetails.email || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div>
                                <small className="text-muted">Address</small>
                                <div className="fw-bold">
                                    {state.supplierMultipleAddress?.label || '-'}
                                </div>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="mt-2">
                                <small className="text-muted">Payment Terms</small>
                                <div className="fw-bold">{state.paymentterms || '-'}</div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Branch & Warehouse */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Branch & Warehouse</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row>
                        <Col md={6}>
                            <div className="mb-2">
                                <small className="text-muted">Branch</small>
                                <div className="fw-bold">{state.branch?.label || '-'}</div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-2">
                                <small className="text-muted">Warehouse</small>
                                <div className="fw-bold">{state.warehouse?.label || '-'}</div>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="mt-2">
                                <small className="text-muted">Delivery Address</small>
                                <div className="fw-bold">{state.shipto || '-'}</div>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="mt-2">
                                <small className="text-muted">Delivery Terms</small>
                                <div className="fw-bold">{state.deliveryterm || '-'}</div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Product Items */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Product Items</h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center fw-bold py-2">#</th>
                                    <th className="fw-bold py-2">Product</th>
                                    <th className="fw-bold py-2">Serial No</th>
                                    <th className="fw-bold py-2">Model No</th>
                                    <th className="fw-bold py-2">Tag No</th>
                                    <th className="fw-bold py-2">Part No</th>
                                    <th className="fw-bold py-2">MOC</th>
                                    <th className="fw-bold py-2">Item Code</th>
                                    <th className="fw-bold py-2">HSN/SAC</th>
                                    <th className="fw-bold py-2 text-center">UOM</th>
                                    <th className="fw-bold py-2 text-center">Currency</th>
                                    <th className="fw-bold py-2 text-center">Qty</th>
                                    <th className="fw-bold py-2 text-end">Unit Rate (FC)</th>
                                    <th className="fw-bold py-2 text-end">Total (FC)</th>
                                    <th className="fw-bold py-2 text-end">Unit Rate ₹</th>
                                    <th className="fw-bold py-2 text-end">Total ₹</th>
                                </tr>
                            </thead>
                            <tbody>
                                {state.rows.map((row, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                            <div className="fw-semibold">{row.product.name}</div>
                                            {row.productremarks && (
                                                <div className="text-muted small mt-1">
                                                    <small><strong>Remarks:</strong> {row.productremarks}</small>
                                                </div>
                                            )}
                                        </td>
                                        <td>{row.serialnumber || '-'}</td>
                                        <td>{row.modelnumber || '-'}</td>
                                        <td>{row.tagnumber || '-'}</td>
                                        <td>{row.partno || '-'}</td>
                                        <td>{row.moc || '-'}</td>
                                        <td>{row.itemcode || '-'}</td>
                                        <td>{row.hsnsacno || '-'}</td>
                                        <td className="text-center">{unitOptions.find(u => u.value === row.uom)?.label || row.uom}</td>
                                        <td className="text-center fw-bold">{row.currency || '-'}</td>
                                        <td className="text-center">{row.quantity}</td>
                                        <td className="text-end">{row.unitratecurrency?.toFixed(2)}</td>
                                        <td className="text-end fw-bold">{row.totalcurrency?.toFixed(2)}</td>
                                        <td className="text-end">{formatCurrency(row.unitrateinr)}</td>
                                        <td className="text-end fw-bold">{formatCurrency(row.totalinr)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <td colSpan="13" className="text-end fw-bold py-2">
                                        Grand Total (Foreign Currency):
                                    </td>
                                    <td className="text-end fw-bold text-primary py-2">
                                        {state.grandtotalcurrency?.toFixed(2)}
                                    </td>
                                    <td className="text-end fw-bold py-2">
                                        Grand Total (INR):
                                    </td>
                                    <td className="text-end fw-bold text-primary py-2">
                                        {formatCurrency(state.grandtotalinr)}
                                    </td>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate(-1)}
                >
                    <FaArrowLeft className="me-1" />
                    Back to List
                </button>
            </div>
        </Container>
    );
};

export default POPIView;